import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@devinforius/i-essentials';
import { Scan, ScanSplit } from '@devinforius/i-scan';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import { GlobalCentreNumerisationDTO } from 'src/app/shared/models/iCourrier/centre-numerisation/global-centre-numerisation';
import { MeRightMail } from 'src/app/shared/models/iCourrier/me/right-mail-id';
import { AttachScanToExistingMailComponentParams } from 'src/app/shared/models/iCourrier/scans/attach-scan-to-mail';
import { ScanSearchMails } from 'src/app/shared/models/iCourrier/scans/scan-search-mails';
import { Scan as ScanDTO } from 'src/app/shared/models/iScanAPI/scan';
import { ScanSearchMailsDTO } from 'src/app/shared/interfaces/scans/scan-search-mail-dto.interface';
import { ICourrierScanService } from 'src/app/shared/services/iCourrier-scan.service';
import { IScanAPIService } from 'src/app/shared/services/iscan.service';
import { MailsRightsService } from 'src/app/shared/services/mails-rights.service';
import { MailsService } from 'src/app/shared/services/mails.service';

@Component({
  selector: 'app-attach-scan-to-existing-mail',
  templateUrl: './attach-scan-to-existing-mail.component.html',
  styleUrls: ['./attach-scan-to-existing-mail.component.scss'],
})
export class AttachScanToExistingMailComponent implements OnChanges {
  @Input() public originScan: ScanDTO;
  @Input() public scan: Scan;
  @Input() public chosenScanSplit: ScanSplit;
  @Input() public expressionReguliereFull: RegExp;
  @Input() public listOfCenterNums: GlobalCentreNumerisationDTO[];

  @Output() public closeModal: EventEmitter<ScanSearchMailsDTO | null> = new EventEmitter();

  public attachForm: FormGroup = new FormGroup({
    linkMailId: new FormControl(null, Validators.required),
  });

  public noQRCode = true;
  public mailNotFoundWithQRCode = false;
  public idFound: number;
  public selectedMailRight: MeRightMail = null;
  public foundMailByQRCodeId: ScanSearchMailsDTO;
  public results = null;
  public scanningCenterId: number;
  public qrCodeFound: string;

  constructor(
    private mailsService: MailsService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private confirmationService: ConfirmationService,
    private iCourrierScanService: ICourrierScanService,
    private iScanAPIService: IScanAPIService,
    private router: Router,
    private mailsRightsService: MailsRightsService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.originScan?.currentValue && changes.chosenScanSplit?.currentValue) {
      this.initDatas();
    }
  }

  private initDatas(): void {
    const index = this.originScan.splits.findIndex((split) => split.guid === this.chosenScanSplit.guid);
    const qrCode = this.originScan.splits[index]?.mainCode;
    if (qrCode !== '') {
      this.noQRCode = false;
      this.settingDatasFromQRCodeAttachToMail(qrCode);
    } else {
      this.noQRCode = true;
    }
  }

  settingDatasFromQRCodeAttachToMail(qrCode: string): void {
    //Si on a un QRCode alors on va essayer de le décomposer afin d'avoir un id
    const rx = new RegExp('ECOURRIER(\\s|.)*\\*ID > (\\d*)(\\s|.)*');
    if (qrCode) {
      qrCode = qrCode.toUpperCase();
      if (rx.test(qrCode)) {
        const id = qrCode.replace(rx, '$2');
        if (id && id !== '' && !Number.isNaN(+id)) {
          //une fois qu'on a un id correct, on va faire la requête afin de voir si l'id du courrier existe bel et bien
          this.mailsService.searchMailsToAttach({ ids: [+id] }).subscribe({
            next: (res) => {
              if (res?.length > 0) {
                //Ensuite on va checker le droit d'ajout de fichier pour la fiche courrier
                this.mailsRightsService.getRightsMailByID(+id).subscribe({
                  next: (response) => {
                    this.selectedMailRight = response;
                    //si on a le droit de le sélectionner, alors on va afficher une version différente de la modale
                    if (this.selectedMailRight.files.add) {
                      this.attachForm.patchValue({ linkMailId: +id + '-(' + res[0].typeEntrantSortant + ')' });
                      this.noQRCode = false;
                      this.foundMailByQRCodeId = res[0];
                    } else {
                      //Si on en a pas le droit, on garde la modale de base.
                      this.noQRCode = true;
                      this.idFound = +id;
                    }
                  },
                  error: (error) => {
                    console.error(error);
                  },
                });
              } else {
                this.noQRCode = true;
                //mais on va tout de même afficher qu'un id a été trouvé
                this.mailNotFoundWithQRCode = true;
                this.idFound = +id;
              }
            },
          });
        } else {
          //Si l'id n'est pas un nombre => y'a une erreur dans le QRCode => on affiche simplement la modale de choix de courrier
          this.noQRCode = true;
        }
      } else {
        //Si le qrcode ne passe pas, on affiche simplement la modale de choix de courrier
        this.noQRCode = true;
      }
    } else {
      this.setManually();
    }
  }

  searchMailAutocomplete(event): void {
    const data = {
      idKey: event.query,
      startWith: true,
    };
    this.mailsService.searchMailsToAttach(data).subscribe({
      next: (res) => {
        if (res.length > 0) {
          this.results = this.filterMail(res);
        } else {
          this.toastr.error(this.translate.translate('homeCourrierChild.noMailWasFound'));
        }
      },
    });
  }

  filterMail(mails: ScanSearchMailsDTO[]): string[] {
    const filtered: string[] = [];
    for (let i = 0; i < mails.length; i++) {
      const mail = new ScanSearchMails(mails[i]);
      if (mail.typeEntrantSortant === 'E') {
        mail.title = mail.id + '-(Entrants)';
      }
      if (mail.typeEntrantSortant === 'S') {
        mail.title = mail.id + '-(Sortants)';
      }
      if (mail.typeEntrantSortant === 'I') {
        mail.title = mail.id + '-(Interne)';
      }
      filtered.push(mail.title);
    }
    return filtered;
  }

  // On vérifie si on a les droits d'ajouter un fichier sur le courrier sélectionné
  onSelectMail(): void {
    this.mailsRightsService.getRightsMailByID(+this.attachForm.value.linkMailId.split('-')[0]).subscribe({
      next: (response) => {
        this.selectedMailRight = response;
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  changeToAttachManual(): void {
    this.confirmationService.confirm({
      message: "Attention, choisir manuellement un id de fiche courrier remplacera l'id trouvé par le QRCode. Voulez-vous continuer ?",
      accept: () => {
        this.setManually();
      },
    });
  }

  submitAttachForm(): void {
    this.scan.splits = this.scan.splits.map((split) => {
      if (split.guid === this.chosenScanSplit.guid) split.lock = true;
      return split;
    });
    this.iScanAPIService.saveSplit(this.scan.guid, this.scan.splits).subscribe({
      next: () => {
        this.setQrCodeDatas();
        const scanLotProcessed = this.scan.splits.filter((split) => split.guid !== this.chosenScanSplit.guid).every((split) => split.lock);
        const data: AttachScanToExistingMailComponentParams = {
          lotGuid: this.originScan.guid,
          courrierId: +this.attachForm.value.linkMailId.split('-')[0],
          splitGuid: this.chosenScanSplit.guid,
          fileDescription: this.chosenScanSplit.name,
          barCode: this.qrCodeFound,
          centreNumerisationId: this.scanningCenterId ? this.scanningCenterId : null,
        };
        if (data.centreNumerisationId === null) {
          delete data.centreNumerisationId;
        }
        this.iCourrierScanService.attachScanToMail(scanLotProcessed, data).subscribe({
          next: () => {
            this.toastr.success(this.translate.translate('thankYou'), this.translate.translate('iScan.success.successFileAssociatedMail'));

            if (scanLotProcessed) {
              this.toastr.success(this.translate.translate('iScan.information.scanCompleted'), this.translate.translate('iScan.success.success'));
              this.router.navigateByUrl('private/ic/home-scans');
            } else {
              this.mailsService.searchMailsToAttach({ ids: [data.courrierId] }).subscribe({
                next: (mails) => {
                  this.closeModal.emit(mails[0]);
                },
              });
            }
          },
          error: () => {
            this.scan.splits = this.scan.splits.map((split) => {
              if (split.guid === this.chosenScanSplit.guid) split.lock = false;
              return split;
            });
            this.iScanAPIService.saveSplit(this.scan.guid, this.scan.splits).subscribe();
            this.toastr.error(this.translate.translate('errorOccurred'));
          },
        });
      },
    });
  }

  setQrCodeDatas(): void {
    if (this.chosenScanSplit?.details?.title === 'QR code') {
      const index = this.originScan.splits.findIndex((split) => split.guid === this.chosenScanSplit.guid);
      this.qrCodeFound = this.originScan.splits[index]?.mainCode;
      const arrayOfBalisesAndContent = [];
      const values = this.qrCodeFound?.match(this.expressionReguliereFull);

      // Puis, pour chaque occurrence, on va extraire la balise et son contenu
      if (values?.length > 0) {
        values.forEach((item: string) => {
          const arr = item.split(/<([^>]+)>/);
          arrayOfBalisesAndContent.push({ balise: arr[1], content: arr[2] });
        });
      }

      arrayOfBalisesAndContent.forEach((item) => {
        if (item.balise === 'CENTERNUM') {
          this.scanningCenterId = this.listOfCenterNums.find((centerNum) => centerNum.codeQr === item.content)?.id;
        }
      });
    } else this.scanningCenterId = null;
  }

  private setManually(): void {
    this.attachForm.reset();
    this.selectedMailRight = null;
    this.noQRCode = true;
    this.foundMailByQRCodeId = null;
    this.idFound = null;
  }
}
